import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import SettingFormItems, {
	SettingItem,
	SettingItems,
} from "../../components/SettingFormItems";
import { findWidgetByName } from "../../utils";
import { updateWidgetItem } from "../../utils/requests";
import { showToastV2 } from "../WidgetBuilder";

interface FAQItem {
	title: string;
	info: string;
}

type SettingsNames =
	| "hideBrand"
	| "hideReview"
	| "hideRelatedProducts"
	| "hideSimilarProducts"
	| "showWhatsappButton"
	| "mobileButtonsType"
	| "showFAQ"
	| "showPriceRange";

type CardSettingsNames =
	| "hideBrand"
	| "hideReview"
	| "cardWidth"
	| "cardHeight"
	| "addToCart"
	| "buyNow"
	| "showDiscount";

type DiscountTagSettingsNames =
	| "discountType"
	| "discountTagBGColor"
	| "discountTagTextColor";

const getProductSettings = (
	isFAQEnabled: boolean
): SettingItem<SettingsNames>[] => {
	const baseSettings: SettingItem<SettingsNames>[] = [
		{
			name: "Hide Brand",
			key: "hideBrand",
			type: "checkbox",
		},
		{
			name: "Hide Review",
			key: "hideReview",
			type: "checkbox",
		},
		{
			name: "Hide Related Products",
			key: "hideRelatedProducts",
			type: "checkbox",
		},
		{
			name: "Hide Similar Products",
			key: "hideSimilarProducts",
			type: "checkbox",
		},
		{
			name: "Show Whatsapp Button",
			key: "showWhatsappButton",
			type: "checkbox",
		},
		{
			name: "Mobile Buttons Type",
			key: "mobileButtonsType",
			type: "select",
			options: [
				{ name: "Fixed Bottom", value: "fixedBottom" },
				{ name: "Default", value: "default" },
			],
		},
		{
			name: "Show Price Range",
			key: "showPriceRange",
			type: "checkbox",
		},
	];

	if (isFAQEnabled) {
		baseSettings.push({
			name: "Show FAQ Section",
			key: "showFAQ",
			type: "checkbox",
		});
	}

	return baseSettings;
};

const cardSettingsItems: SettingItem<CardSettingsNames>[] = [
	{
		name: "Card Image Width",
		type: "number",
		key: "cardWidth",
	},
	{
		name: "Card Image Height",
		type: "number",
		key: "cardHeight",
	},
	{
		name: "Add To Cart Button",
		type: "checkbox",
		key: "addToCart",
	},
	{
		name: "Buy Now Button",
		type: "checkbox",
		key: "buyNow",
	},
	{
		name: "Show Discount",
		key: "showDiscount",
		type: "checkbox",
	},
	{
		name: "Hide Brand",
		key: "hideBrand",
		type: "checkbox",
	},
	{
		name: "Hide Review",
		key: "hideReview",
		type: "checkbox",
	},
];

const discountTagSettings: SettingItem<DiscountTagSettingsNames>[] = [
	{
		name: "Discount Type",
		key: "discountType",
		type: "select",
		options: [
			{ name: "Percentage", value: "percentage" },
			{ name: "Amount", value: "amount" },
		],
	},
	{
		name: "Discount Tag Background Color",
		key: "discountTagBGColor",
		type: "color",
	},
	{
		name: "Discount Tag Text Color",
		key: "discountTagTextColor",
		type: "color",
	},
];

const ProductSettings = () => {
	const shop = useSelector((state: RootState) => state.auth.shop);
	const [settings, setSettings] = useState<SettingItems<SettingsNames>>({});
	const [cardSettings, setCardSettings] = useState<
		SettingItems<CardSettingsNames | DiscountTagSettingsNames>
	>({});
	const [faqItems, setFaqItems] = useState<FAQItem[]>([]);

	const [themeSettingsW, setThemeSettingsW] = useState<any | null>(null);

	const isFAQEnabled =
		shop?.slug === "laorganika" || window.location.hostname === "localhost";

	useEffect(() => {
		findWidgetByName("Theme Settings").then((res) => {
			setThemeSettingsW(res);

			const baseSettings = {
				hideBrand: res?.settings?.productSettings?.hideBrand || false,
				hideReview: res?.settings?.productSettings?.hideReview || false,
				hideRelatedProducts:
					res?.settings?.productSettings?.hideRelatedProducts ||
					false,
				hideSimilarProducts:
					res?.settings?.productSettings?.hideSimilarProducts ||
					false,
				showWhatsappButton:
					res?.settings?.productSettings?.showWhatsappButton || false,
				mobileButtonsType:
					res?.settings?.productSettings?.mobileButtonsType ||
					"fixedBottom",
				showPriceRange:
					res?.settings?.productSettings?.showPriceRange || false,
			};

			if (isFAQEnabled) {
				setSettings({
					...baseSettings,
					showFAQ: res?.settings?.productSettings?.showFAQ || false,
				});
				setFaqItems(res?.settings?.productSettings?.faqItems || []);
			} else {
				setSettings(baseSettings);
			}

			setCardSettings({
				hideBrand: res?.settings?.cardSettings?.hideBrand || false,
				hideReview: res?.settings?.cardSettings?.hideReview || false,
				cardWidth: res?.settings?.cardSettings?.cardWidth || 0,
				cardHeight: res?.settings?.cardSettings?.cardHeight || 0,
				addToCart: res?.settings?.cardSettings?.addToCart || false,
				buyNow: res?.settings?.cardSettings?.buyNow || false,
				showDiscount:
					res?.settings?.cardSettings?.showDiscount || false,
				discountType:
					res?.settings?.cardSettings?.discountType || "percentage",
				discountTagBGColor:
					res?.settings?.cardSettings?.discountTagBGColor || "",
				discountTagTextColor:
					res?.settings?.cardSettings?.discountTagTextColor || "",
			});
		});
	}, []);

	const addFaqItem = () => {
		setFaqItems([...faqItems, { title: "", info: "" }]);
	};

	const updateFaqItem = (
		index: number,
		field: keyof FAQItem,
		value: string
	) => {
		const updatedItems = faqItems.map((item, i) =>
			i === index ? { ...item, [field]: value } : item
		);
		setFaqItems(updatedItems);
	};

	const removeFaqItem = (index: number) => {
		setFaqItems(faqItems.filter((_, i) => i !== index));
	};

	const handlePublish = () => {
		const productSettings = {
			...themeSettingsW.settings?.productSettings,
			...settings,
			...(isFAQEnabled && { faqItems }),
		};

		updateWidgetItem({
			...themeSettingsW,
			status: "published",
			active: true,
			settings: {
				...themeSettingsW.settings,
				productSettings,
				cardSettings,
			},
		})
			.then((res) => {
				showToastV2("Footer updated successfully");
			})
			.catch(() => {
				showToastV2("Footer update failed!", "error");
			});
	};

	return (
		<div className="p-4">
			<div className="sticky top-[60px] left-0 z-50 bg-white py-4 w-full shadow-md px-2 flex justify-between">
				<h1 className="text-2xl font-bold">Product Settings</h1>

				<div className="flex gap-4 items-center">
					{/* <button
						className="bg-gray-500 text-white px-4 py-2 rounded-md"
						onClick={handleToDraft}>
						Safe to draft
					</button> */}

					<button
						className="bg-green-500 text-white px-4 py-2 rounded-md"
						onClick={handlePublish}>
						Publish
					</button>
				</div>
			</div>

			<div className="grid lg:grid-cols-3 gap-4">
				<div className="mt-4 border border-gray-300 px-4 py-1">
					<p className="text-lg font-semibold">
						Product Page Settings :
					</p>
					<SettingFormItems
						settings={settings}
						settingItems={getProductSettings(isFAQEnabled)}
						onChange={(key, value) => {
							setSettings((prev) => ({
								...prev,
								[key]: value,
							}));
						}}
					/>
				</div>

				<div className="mt-4 border border-gray-300 px-4 py-1">
					<p className="text-lg font-semibold">Card Settings :</p>
					<SettingFormItems
						settings={cardSettings}
						settingItems={cardSettingsItems}
						onChange={(key, value) => {
							setCardSettings((prev) => ({
								...prev,
								[key]: value,
							}));
						}}
					/>
				</div>

				{cardSettings?.showDiscount && (
					<div className="mt-4 border border-gray-300 px-4 py-1">
						<p className="text-lg font-semibold">
							Discount Tag Settings :
						</p>
						<SettingFormItems
							settings={cardSettings}
							settingItems={discountTagSettings}
							onChange={(key, value) => {
								setCardSettings((prev) => ({
									...prev,
									[key]: value,
								}));
							}}
						/>
					</div>
				)}

				{isFAQEnabled && settings?.showFAQ && (
					<div className="mt-4 border border-gray-300 px-4 py-1">
						<div className="flex items-center justify-between mb-4">
							<p className="text-lg font-semibold">FAQ Items :</p>
							<button
								type="button"
								onClick={addFaqItem}
								className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600">
								Add FAQ
							</button>
						</div>

						<div className="space-y-4">
							{faqItems.map((faq, index) => (
								<div
									key={index}
									className="border border-gray-200 p-3 rounded">
									<div className="flex justify-between items-center mb-2">
										<span className="text-sm font-medium text-gray-700">
											FAQ #{index + 1}
										</span>
										<button
											type="button"
											onClick={() => removeFaqItem(index)}
											className="text-red-500 hover:text-red-700 text-sm">
											Remove
										</button>
									</div>

									<div className="space-y-2">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Title:
											</label>
											<input
												type="text"
												value={faq.title}
												onChange={(e) =>
													updateFaqItem(
														index,
														"title",
														e.target.value
													)
												}
												placeholder="Enter FAQ title"
												className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
											/>
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Info:
											</label>
											<textarea
												value={faq.info}
												onChange={(e) =>
													updateFaqItem(
														index,
														"info",
														e.target.value
													)
												}
												placeholder="Enter FAQ information"
												rows={3}
												className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 resize-vertical"
											/>
										</div>
									</div>
								</div>
							))}

							{faqItems.length === 0 && (
								<div className="text-center py-4 text-gray-500">
									No FAQ items added yet. Click "Add FAQ" to
									get started.
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default ProductSettings;
